import { getQuery } from 'client/utils/location';
import {
  DISPLAY_PRICE,
  INVENTORY_TYPE,
  PAGE_NUMBER,
  PAGE_SIZE,
  SORT_BY,
  ROOFTOP_ID,
} from 'site-modules/shared/constants/allowed-inventory-request-params';
import { INVENTORY_TYPES } from 'client/constants/inventory-types';
import { DEFAULT_APR, DEFAULT_TERM, DEFAULT_TERM_72 } from 'site-modules/shared/constants/calculator/calculator';

const PRICE_RANGE_FROM_PERCENTAGE = 0.85;
const PRICE_RANGE_TO_PERCENTAGE = 0.95;

export const DEFAULT_PAYMENT_VALUES = {
  monthlyPayment: 500,
  marketFinanceRate: DEFAULT_APR,
  term: DEFAULT_TERM,
  downPayment: 1000,
  owedTradeIn: 0,
  tradeIn: 0,
};

/**
 * Gets params for 'searchFilter'.
 * @param {number} minPrice
 * @param {number} maxPrice
 * @param {object} additionalFilters (optional)
 * @returns {object}
 */
export function getSearchFilterParams(minPrice, maxPrice, additionalFilters) {
  return {
    [DISPLAY_PRICE]: `${minPrice}-${maxPrice}`,
    [INVENTORY_TYPE]: INVENTORY_TYPES.NEW,
    [PAGE_NUMBER]: 1,
    [PAGE_SIZE]: 2,
    skipMakeModel: true,
    ...additionalFilters,
  };
}

export function getSearchFilterParamsWithDealerId(minPrice, maxPrice, dealerId) {
  const additionalFilters = dealerId
    ? {
        [SORT_BY]: 'prices.advertisedPrice:ASC',
        [ROOFTOP_ID]: dealerId,
      }
    : {};
  const searchFilterParams = getSearchFilterParams(minPrice, maxPrice, additionalFilters);

  if (dealerId) {
    delete searchFilterParams[INVENTORY_TYPE]; // This needs to be removed in order to return ALL inventory from the API call when dealerId is supplied
  }

  return searchFilterParams;
}

// TODO: Move calculations to rest service and update code to use it.
/**
 * Returns calculated payments.
 * @param {object} [payments]
 * @param {number} [salesTax=0]
 * @returns {object}
 */
export function calculatePayments({ payments = DEFAULT_PAYMENT_VALUES, salesTax = 0, isCarmaxTraffic = false }) {
  const { monthlyPayment, marketFinanceRate, term, downPayment, owedTradeIn, tradeIn } = payments;
  const totalDownPayment = downPayment + tradeIn - owedTradeIn;
  const stickerPrice = (1 - (marketFinanceRate / 100 + salesTax)) * monthlyPayment * term + totalDownPayment;
  const stickerPriceMin = isCarmaxTraffic ? 0 : PRICE_RANGE_FROM_PERCENTAGE * stickerPrice;
  const stickerPriceMax = PRICE_RANGE_TO_PERCENTAGE * stickerPrice;

  return {
    totalDownPayment,
    stickerPriceMin,
    stickerPriceMax,
  };
}

export function getCalculatorPaymentValues({ location, monthlyPayment, defaultLoanTerm72mo }) {
  const { payment, tradein, payoff, downpayment, apr } = getQuery(location);
  return {
    ...DEFAULT_PAYMENT_VALUES,
    term: defaultLoanTerm72mo ? DEFAULT_TERM_72 : DEFAULT_TERM,
    monthlyPayment: Number(payment || monthlyPayment || DEFAULT_PAYMENT_VALUES.monthlyPayment),
    tradeIn: Number(tradein || DEFAULT_PAYMENT_VALUES.tradeIn),
    owedTradeIn: Number(payoff || DEFAULT_PAYMENT_VALUES.owedTradeIn),
    downPayment: Number(downpayment || DEFAULT_PAYMENT_VALUES.downPayment),
    marketFinanceRate: Number(apr || DEFAULT_PAYMENT_VALUES.marketFinanceRate),
  };
}

export function getIsCarmaxTraffic(location) {
  const { dealergroup } = getQuery(location);
  return Boolean(dealergroup && dealergroup.toLowerCase() === 'carmax');
}
